import React from 'react';
import { Shape } from 'react-konva';
import Konva from 'konva';
import { useAppSelector } from '../../hooks/redux';
import { aciToHex } from '../../utils/dxf';
import { computePolylineBBoxes } from '../../utils/floorplanHitTest';

/**
 * Renders the imported DXF floor plan as a non-interactive background layer.
 *
 * Performance strategy for very large plans (the external DXF→JSON parser can
 * emit 100k+ polylines / millions of points):
 *
 *  1. SINGLE SHAPE — every polyline is drawn in one custom Konva.Shape via a
 *     sceneFunc, instead of one <Line> node each. One node, one path, one draw.
 *
 *  2. VIEWPORT CULLING — a bounding box is precomputed for each polyline once.
 *     On every draw we skip polylines whose bbox doesn't intersect the visible
 *     world rectangle, so cost scales with what's on screen (crucial when the
 *     user zooms in to inspect a small area).
 *
 *  3. PER-LAYER BATCHING — polylines are grouped by DXF layer so each layer is
 *     stroked once in its own ACI color and hidden layers are skipped entirely.
 *
 *  4. CONSTANT STROKE — line width is divided by the absolute stage scale so
 *     strokes stay ~1px on screen at any zoom (like strokeScaleEnabled=false).
 *
 * This component is expected to live in its own listening={false} Layer so that
 * element interactions (hover/select/drag) never trigger a floor-plan redraw.
 */
const FloorPlan: React.FC = () => {
  const polylines = useAppSelector((state) => state.floorplan.polylines);
  const polylineLayers = useAppSelector((state) => state.floorplan.polylineLayers);
  const polylineMeta = useAppSelector((state) => state.floorplan.polylineMeta);
  const layers = useAppSelector((state) => state.floorplan.layers);
  const visible = useAppSelector((state) => state.floorplan.visible);
  const hiddenEntityTypes = useAppSelector((state) => state.floorplan.hiddenEntityTypes);
  const hoveredLayerIndex = useAppSelector((state) => state.floorplan.hoveredLayerIndex);
  const opacity = useAppSelector((state) => state.floorplan.opacity);
  // Default stroke for ByLayer/ByBlock/white (ACI 0/7/256) colors — light mode only.
  const defaultStroke = '#475569';

  // Precompute a [minX, minY, maxX, maxY] bbox per polyline (once per dataset).
  const bboxes = React.useMemo(() => computePolylineBBoxes(polylines), [polylines]);

  // Resolve each layer's stroke color (ACI → hex, falling back to the theme).
  const layerColors = React.useMemo(
    () => layers.map((l) => aciToHex(l.color) ?? defaultStroke),
    [layers, defaultStroke]
  );

  // Group polyline indices by layer + resolved stroke color once. Entity color
  // (ACI) wins; ByLayer/ByBlock/white falls back to the owning layer color.
  const colorBucketsByLayer = React.useMemo(() => {
    const map: Array<Map<string, number[]>> = [];
    for (let li = 0; li < layers.length; li++) map.push(new Map<string, number[]>());
    for (let p = 0; p < polylineLayers.length; p++) {
      const li = polylineLayers[p];
      if (li < 0 || li >= map.length) continue;
      const entityStroke = aciToHex(polylineMeta[p]?.color ?? 256);
      const stroke = entityStroke ?? layerColors[li] ?? defaultStroke;
      const bucket = map[li].get(stroke);
      if (bucket) {
        bucket.push(p);
      } else {
        map[li].set(stroke, [p]);
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polylineLayers, polylineMeta, polylines.length, layers.length, layerColors, defaultStroke]);

  const sceneFunc = React.useCallback(
    (context: Konva.Context, shape: Konva.Shape) => {
      if (polylines.length === 0) return;
      const stage = shape.getStage();
      if (!stage) return;

      const ctx = context._context; // native CanvasRenderingContext2D
      const scale = stage.scaleX() || 1;
      const pos = stage.position();

      // Visible world rectangle (+ small margin so edge lines aren't clipped).
      const margin = 64 / scale;
      const viewLeft = -pos.x / scale - margin;
      const viewTop = -pos.y / scale - margin;
      const viewRight = viewLeft + stage.width() / scale + 2 * margin;
      const viewBottom = viewTop + stage.height() / scale + 2 * margin;

      // LOD: when zoomed out, skip tiny features and thin vertex streams so
      // overview pans stay interactive on 100k+ polyline plans.
      const minBboxPx = 1.25;
      const minBboxWorld = minBboxPx / scale;
      const pointStride = scale < 0.04 ? 6 : scale < 0.1 ? 4 : scale < 0.25 ? 2 : 1;

      const hasHovered =
        hoveredLayerIndex !== null && hoveredLayerIndex >= 0 && hoveredLayerIndex < colorBucketsByLayer.length;

      // Draw non-hovered layers (or all layers if none is hovered)
      for (let li = 0; li < colorBucketsByLayer.length; li++) {
        if (hasHovered && li === hoveredLayerIndex) continue;

        const layer = layers[li];
        if (layer && layer.visible === false) continue;
        const buckets = colorBucketsByLayer[li];
        if (!buckets || buckets.size === 0) continue;

        for (const [strokeColor, idxs] of buckets.entries()) {
          ctx.beginPath();
          let drew = false;
          for (let n = 0; n < idxs.length; n++) {
            const p = idxs[n];
            const o = p * 4;
            // Cull polylines whose bbox is fully outside the viewport.
            if (
              bboxes[o + 2] < viewLeft ||
              bboxes[o] > viewRight ||
              bboxes[o + 3] < viewTop ||
              bboxes[o + 1] > viewBottom
            ) {
              continue;
            }
            // LOD: drop sub-pixel features when zoomed out.
            const bw = bboxes[o + 2] - bboxes[o];
            const bh = bboxes[o + 3] - bboxes[o + 1];
            if (bw < minBboxWorld && bh < minBboxWorld) continue;

            // Skip if this entity type has been hidden for this layer.
            const entityType = polylineMeta[p]?.type;
            if (entityType && hiddenEntityTypes[`${li}:${entityType}`]) continue;

            const pts = polylines[p];
            if (pts.length < 4) continue;
            ctx.moveTo(pts[0], pts[1]);
            const stride = pointStride * 2;
            for (let i = stride; i + 1 < pts.length; i += stride) {
              ctx.lineTo(pts[i], pts[i + 1]);
            }
            // Always finish at the true endpoint so closed shapes don't open.
            const last = pts.length - 2;
            if (last >= 2 && (last % stride !== 0)) {
              ctx.lineTo(pts[last], pts[last + 1]);
            }
            drew = true;
          }
          if (drew) {
            ctx.lineWidth = 1 / scale;
            ctx.globalAlpha = hasHovered ? opacity * 0.25 : opacity;
            ctx.strokeStyle = strokeColor;
            ctx.stroke();
          }
        }
      }

      // Draw the hovered layer on top of everything
      if (hasHovered) {
        const li = hoveredLayerIndex!;
        const layer = layers[li];
        const buckets = colorBucketsByLayer[li];
        if (layer && layer.visible !== false && buckets && buckets.size > 0) {
          for (const [strokeColor, idxs] of buckets.entries()) {
            ctx.beginPath();
            let drew = false;
            for (let n = 0; n < idxs.length; n++) {
              const p = idxs[n];
              const o = p * 4;
              // Cull polylines whose bbox is fully outside the viewport.
              if (
                bboxes[o + 2] < viewLeft ||
                bboxes[o] > viewRight ||
                bboxes[o + 3] < viewTop ||
                bboxes[o + 1] > viewBottom
              ) {
                continue;
              }
              // Skip if this entity type has been hidden for this layer.
              const entityType = polylineMeta[p]?.type;
              if (entityType && hiddenEntityTypes[`${li}:${entityType}`]) continue;

              const pts = polylines[p];
              if (pts.length < 4) continue;
              ctx.moveTo(pts[0], pts[1]);
              for (let i = 2; i + 1 < pts.length; i += 2) {
                ctx.lineTo(pts[i], pts[i + 1]);
              }
              drew = true;
            }
            if (drew) {
              ctx.lineWidth = 3.5 / scale;
              ctx.globalAlpha = 1;
              ctx.strokeStyle = strokeColor;
              ctx.stroke();
            }
          }
        }
      }

      ctx.globalAlpha = 1;
    },
    [
      polylines,
      bboxes,
      colorBucketsByLayer,
      layers,
      polylineMeta,
      hiddenEntityTypes,
      hoveredLayerIndex,
      opacity,
    ]
  );

  if (!visible || polylines.length === 0) return null;

  return <Shape sceneFunc={sceneFunc} listening={false} perfectDrawEnabled={false} />;
};

export default React.memo(FloorPlan);
